import React, { useState, useEffect } from 'react';
import {
  getAutomations,
  getAutomationTemplates,
  createAutomationFromTemplate,
  toggleAutomation,
  deleteAutomation,
  type Automation,
  type AutomationTemplate,
  getTriggerTypeLabel,
  getEventTypeLabel,
} from '../services/automationService';
import { Link } from 'react-router-dom';
import {
  PlayIcon,
  PauseIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ClockIcon,
  BoltIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const AutomationsPage: React.FC = () => {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadAutomations();
    loadTemplates();
  }, [activeFilter]);

  const loadAutomations = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (activeFilter === 'active') filters.isActive = true;
      if (activeFilter === 'inactive') filters.isActive = false;
      
      const data = await getAutomations(filters);
      setAutomations(data);
    } catch (error) {
      console.error('Failed to load automations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await getAutomationTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleToggleAutomation = async (id: string, isActive: boolean) => {
    try {
      await toggleAutomation(id, !isActive);
      loadAutomations();
    } catch (error) {
      console.error('Failed to toggle automation:', error);
      alert('Failed to toggle automation');
    }
  };

  const handleDeleteAutomation = async (id: string) => {
    if (!confirm('Are you sure you want to delete this automation?')) {
      return;
    }

    try {
      await deleteAutomation(id);
      loadAutomations();
    } catch (error) {
      console.error('Failed to delete automation:', error);
      alert('Failed to delete automation');
    }
  };

  const handleUseTemplate = async (templateId: string) => {
    try {
      await createAutomationFromTemplate(templateId);
      setShowTemplates(false);
      loadAutomations();
    } catch (error) {
      console.error('Failed to create automation from template:', error);
      alert('Failed to create automation from template');
    }
  };

  const getTriggerIcon = (triggerType: string) => {
    switch (triggerType) {
      case 'event':
        return <BoltIcon className="h-5 w-5 text-blue-500" />;
      case 'schedule':
        return <ClockIcon className="h-5 w-5 text-green-500" />;
      default:
        return <PlayIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Paused
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Automations</h1>
            <p className="mt-2 text-sm text-gray-600">
              Automate your workflow with triggers, conditions, and actions
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Browse Templates
            </button>
            <Link
              to="/automations/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Create Automation
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setActiveFilter('all')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'all'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All ({automations.length})
          </button>
          <button
            onClick={() => setActiveFilter('active')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'active'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setActiveFilter('inactive')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeFilter === 'inactive'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Paused
          </button>
        </div>
      </div>

      {/* Templates Modal */}
      {showTemplates && (
        <div className="mb-8 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Automation Templates</h2>
            <button
              onClick={() => setShowTemplates(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getTriggerIcon(template.triggerType)}
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  </div>
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                    {template.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {template.actions.length} action{template.actions.length !== 1 ? 's' : ''}
                  </div>
                  <button
                    onClick={() => handleUseTemplate(template.id)}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Use Template →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Automations List */}
      {automations.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <BoltIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No automations</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new automation or using a template.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowTemplates(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Browse Templates
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {automations.map((automation) => (
            <div
              key={automation.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="mt-1">{getTriggerIcon(automation.triggerType)}</div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {automation.name}
                      </h3>
                      {getStatusBadge(automation.isActive)}
                    </div>
                    {automation.description && (
                      <p className="text-sm text-gray-600 mb-3">{automation.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">Trigger:</span>
                        <span className="ml-1">
                          {getTriggerTypeLabel(automation.triggerType as any)}
                          {automation.triggerConfig.eventType && (
                            <> - {getEventTypeLabel(automation.triggerConfig.eventType as any)}</>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">Actions:</span>
                        <span className="ml-1">{automation.actions.length}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-gray-700">Executions:</span>
                        <span className="ml-1">{automation.executionCount}</span>
                      </div>
                      {automation.lastExecutedAt && (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-700">Last run:</span>
                          <span className="ml-1">
                            {new Date(automation.lastExecutedAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <Link
                    to={`/automations/${automation.id}/stats`}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                    title="View Stats"
                  >
                    <ChartBarIcon className="h-5 w-5" />
                  </Link>
                  <Link
                    to={`/automations/${automation.id}/edit`}
                    className="p-2 text-gray-400 hover:text-indigo-600 rounded-md hover:bg-indigo-50"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => handleToggleAutomation(automation.id, automation.isActive)}
                    className={`p-2 rounded-md ${
                      automation.isActive
                        ? 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50'
                        : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                    }`}
                    title={automation.isActive ? 'Pause' : 'Activate'}
                  >
                    {automation.isActive ? (
                      <PauseIcon className="h-5 w-5" />
                    ) : (
                      <PlayIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteAutomation(automation.id)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutomationsPage;
